import { useEffect } from 'react';
import airbridge from 'airbridge-web-sdk-loader';
import { sentry } from '@myrealtrip/logger';

import { AIRBRIDGE_APP_NAME, AIRBRIDGE_WEB_TOKEN } from './config';

const RECENT_UTM_SOURCE_COOKIE = 'recent_utm_source';

/**
 * 현재 페이지의 UTM 소스 기본값을 해석한다.
 *
 * 우선순위: `recent_utm_source` 쿠키 > `utm_source` 쿼리 파라미터 > `fallback`
 */
function resolveDefaultChannel(fallback?: string): string | undefined {
  if (typeof window === 'undefined') {
    return fallback;
  }

  const cookieMatch = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${RECENT_UTM_SOURCE_COOKIE}=`));
  if (cookieMatch) {
    // 쿠키값에 `=` 가 포함될 수 있어(base64 패딩 등) 첫 `=` 이후 전체를 취한다
    const rawValue = cookieMatch.slice(RECENT_UTM_SOURCE_COOKIE.length + 1);
    const value = decodeURIComponent(rawValue);
    if (value) return value;
  }

  const utmSource = new URLSearchParams(window.location.search).get('utm_source');
  if (utmSource) return utmSource;

  return fallback;
}

interface UseAirbridgeOptions {
  /**
   * `recent_utm_source` 쿠키와 `utm_source` 쿼리 파라미터가 모두 없을 때 사용할 기본 채널명.
   */
  defaultMediaSourceValue?: string;
}

/** airbridge init은 비동기 방식으로 처리되기 때문에 초기화 중인 경우 중복 초기화를 방지하기 위해 사용 */
let isAirbridgeInitializing = false;

/**
 * Airbridge Web SDK 초기화 훅
 *
 * 각 앱의 `_app.tsx`에서 한 번만 호출한다.
 * 앱 도메인별로 별도 Next.js 앱이 구동되므로 각 앱마다 초기화가 필요하다.
 *
 * 초기화는 앱 생애주기 중 1회만 수행되며, 이후 `defaultMediaSourceValue`가
 * 변경되어도 재초기화하지 않는다(초기 렌더 시점의 값만 사용).
 */
export default function useAirbridge({ defaultMediaSourceValue }: UseAirbridgeOptions = {}) {
  useEffect(() => {
    if (!airbridge) {
      return;
    }

    // 이미 초기화 완료 또는 진행 중이면 스킵. === 비교로 SDK 타입 변경에 대해서도 안전하게 방어.
    if (airbridge.isSDKEnabled === true || isAirbridgeInitializing) {
      return;
    }

    isAirbridgeInitializing = true;
    try {
      airbridge.init(
        {
          app: AIRBRIDGE_APP_NAME,
          webToken: AIRBRIDGE_WEB_TOKEN,
          defaultChannel: resolveDefaultChannel(defaultMediaSourceValue),
          utmParsing: true,
          useProtectedAttributionWindow: true,
        },
        () => {
          isAirbridgeInitializing = false;
        },
      );
    } catch (error) {
      isAirbridgeInitializing = false;
      sentry.capture({
        error: error instanceof Error ? error : new Error(JSON.stringify({ error })),
        level: 'error',
      });
    }
    // 생애주기 중 1회만 초기화. defaultMediaSourceValue 변경에 따른 재실행은 의도적으로 제외.
  }, []);
}
